<template>
  <div class="app-container">
    <h1>Ustawienia AI</h1>

    <!-- Global Default Provider -->
    <el-card v-loading="loading" class="box-card" style="margin-bottom: 20px;">
      <div slot="header" class="clearfix">
        <span><strong>Domyślny dostawca AI</strong></span>
        <span style="font-size: 12px; color: #909399; margin-left: 8px;">
          (używany gdy zadanie nie ma własnych ustawień)
        </span>
      </div>

      <el-form label-width="180px" label-position="left">
        <el-form-item label="Dostawca">
          <el-select v-model="form.provider" placeholder="Wybierz dostawcę" @change="onProviderChange">
            <el-option
              v-for="p in availableProviders"
              :key="p.value"
              :label="p.label"
              :value="p.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Model">
          <el-select v-model="form.model" placeholder="Wybierz model">
            <el-option
              v-for="m in currentModels"
              :key="m.value"
              :label="m.label"
              :value="m.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="saveSettings" :loading="saving">
            Zapisz ustawienia
          </el-button>
          <el-button type="info" @click="testConnection(null)" :loading="testing">
            Testuj połączenie
          </el-button>
        </el-form-item>
      </el-form>

      <!-- Test result -->
      <div v-if="testResult && !testTaskKey" style="margin-top: 16px;">
        <el-alert
          :title="testResult.success ? 'Połączenie działa' : 'Błąd połączenia'"
          :type="testResult.success ? 'success' : 'error'"
          :description="testResultDescription"
          show-icon
          :closable="false"
        />
      </div>
    </el-card>

    <!-- Per-Task Settings -->
    <el-card v-loading="loading" class="box-card" style="margin-bottom: 20px;">
      <div slot="header" class="clearfix">
        <span><strong>Ustawienia AI per zadanie</strong></span>
        <span style="font-size: 12px; color: #909399; margin-left: 8px;">
          (opcjonalnie — puste = używa domyślnych)
        </span>
      </div>

      <div v-for="(task, taskKey) in taskSettings" :key="taskKey" class="task-row">
        <el-form label-width="180px" label-position="left" style="margin-bottom: 0;">
          <div class="task-header">
            <el-tag size="medium" type="info">{{ task.label }}</el-tag>
            <span v-if="!task.provider" class="task-default-badge">→ domyślny</span>
            <span v-else class="task-custom-badge">{{ task.provider }} / {{ task.model }}</span>
          </div>

          <el-form-item label="Dostawca">
            <el-select
              v-model="task.provider"
              placeholder="Domyślny"
              clearable
              @change="onTaskProviderChange(taskKey)"
            >
              <el-option
                v-for="p in availableProviders"
                :key="p.value"
                :label="p.label"
                :value="p.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="Model">
            <el-select
              v-model="task.model"
              placeholder="Domyślny"
              clearable
              :disabled="!task.provider"
            >
              <el-option
                v-for="m in getTaskModels(taskKey)"
                :key="m.value"
                :label="m.label"
                :value="m.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" size="small" @click="saveTaskSettings(taskKey)" :loading="savingTask === taskKey">
              Zapisz
            </el-button>
            <el-button type="info" size="small" @click="testConnection(taskKey)" :loading="testing && testTaskKey === taskKey">
              Testuj
            </el-button>
            <el-button v-if="task.provider" type="warning" size="small" plain @click="resetTaskSettings(taskKey)">
              Resetuj do domyślnych
            </el-button>
          </el-form-item>
        </el-form>

        <!-- Test result for this task -->
        <div v-if="testResult && testTaskKey === taskKey" style="margin-top: 8px;">
          <el-alert
            :title="testResult.success ? 'Połączenie działa' : 'Błąd połączenia'"
            :type="testResult.success ? 'success' : 'error'"
            :description="testResultDescription"
            show-icon
            :closable="false"
          />
        </div>
      </div>
    </el-card>

    <!-- Usage Stats -->
    <el-card class="box-card" style="margin-bottom: 20px;">
      <div slot="header" class="clearfix">
        <span><strong>Statystyki użycia AI</strong></span>
      </div>

      <el-row :gutter="20">
        <el-col :span="8">
          <div class="stat-card">
            <div class="stat-number">{{ stats.today }}</div>
            <div class="stat-label">Dzisiaj</div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-card">
            <div class="stat-number">{{ stats.week }}</div>
            <div class="stat-label">Ostatnie 7 dni</div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-card">
            <div class="stat-number">{{ stats.month }}</div>
            <div class="stat-label">Ostatnie 30 dni</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- Top Users Today -->
    <el-row :gutter="20">
      <el-col :span="12">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span><strong>Najwięcej generacji dziś (użytkownicy)</strong></span>
          </div>
          <el-table :data="topUsers" border size="small" v-if="topUsers.length > 0">
            <el-table-column prop="email" label="Email użytkownika">
              <template slot-scope="scope">
                {{ scope.row.email || scope.row.user_id }}
              </template>
            </el-table-column>
            <el-table-column prop="count" label="Liczba generacji" />
          </el-table>
          <p v-else class="no-data">Brak danych</p>
        </el-card>
      </el-col>

      <el-col :span="12">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span><strong>Najwięcej generacji dziś (IP)</strong></span>
          </div>
          <el-table :data="topIps" border size="small" v-if="topIps.length > 0">
            <el-table-column prop="ip_address" label="Adres IP" />
            <el-table-column prop="count" label="Liczba generacji" />
          </el-table>
          <p v-else class="no-data">Brak danych</p>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getAiSettings, updateAiSettings, updateTaskAiSettings, testAiConnection } from '@/api/ai-settings';

export default {
  name: 'AiSettings',
  data() {
    return {
      loading: true,
      saving: false,
      savingTask: null,
      testing: false,
      testTaskKey: null,
      form: {
        provider: 'deepseek',
        model: 'deepseek-v4-flash',
      },
      taskSettings: {},
      availableProviders: [],
      stats: { today: 0, week: 0, month: 0 },
      topUsers: [],
      topIps: [],
      testResult: null,
    };
  },
  computed: {
    currentModels() {
      const provider = this.availableProviders.find(p => p.value === this.form.provider);
      return provider ? provider.models : [];
    },
    testResultDescription() {
      if (!this.testResult) return '';
      if (this.testResult.success) {
        return `Provider: ${this.testResult.provider}, Model: ${this.testResult.model}, Czas odpowiedzi: ${this.testResult.response_time_ms}ms`;
      }
      return `Provider: ${this.testResult.provider}, Model: ${this.testResult.model}, Błąd: ${this.testResult.error}`;
    },
  },
  created() {
    this.fetchSettings();
  },
  methods: {
    async fetchSettings() {
      this.loading = true;
      try {
        const data = await getAiSettings();
        this.form.provider = data.provider;
        this.form.model = data.model;
        this.availableProviders = data.available_providers || [];
        this.taskSettings = data.task_settings || {};
        this.stats = data.stats || { today: 0, week: 0, month: 0 };
        this.topUsers = data.top_users || [];
        this.topIps = data.top_ips || [];
      } catch (e) {
        this.$message.error('Nie udało się pobrać ustawień AI.');
      }
      this.loading = false;
    },
    onProviderChange() {
      const models = this.currentModels;
      if (models.length > 0) {
        this.form.model = models[0].value;
      }
    },
    onTaskProviderChange(taskKey) {
      const task = this.taskSettings[taskKey];
      if (!task.provider) {
        task.model = '';
        return;
      }
      const models = this.getTaskModels(taskKey);
      if (models.length > 0) {
        task.model = models[0].value;
      }
    },
    getTaskModels(taskKey) {
      const task = this.taskSettings[taskKey];
      if (!task || !task.provider) return [];
      const provider = this.availableProviders.find(p => p.value === task.provider);
      return provider ? provider.models : [];
    },
    async saveSettings() {
      this.saving = true;
      try {
        await updateAiSettings(this.form);
        this.$message.success('Domyślne ustawienia AI zostały zaktualizowane.');
      } catch (e) {
        this.$message.error('Nie udało się zapisać ustawień.');
      }
      this.saving = false;
    },
    async saveTaskSettings(taskKey) {
      this.savingTask = taskKey;
      try {
        const task = this.taskSettings[taskKey];
        await updateTaskAiSettings(taskKey, {
          provider: task.provider || '',
          model: task.model || '',
        });
        this.$message.success(`Ustawienia dla "${task.label}" zostały zaktualizowane.`);
      } catch (e) {
        this.$message.error('Nie udało się zapisać ustawień zadania.');
      }
      this.savingTask = null;
    },
    async resetTaskSettings(taskKey) {
      this.savingTask = taskKey;
      try {
        await updateTaskAiSettings(taskKey, { provider: '', model: '' });
        this.taskSettings[taskKey].provider = '';
        this.taskSettings[taskKey].model = '';
        this.$message.success('Ustawienia zadania zresetowane do domyślnych.');
      } catch (e) {
        this.$message.error('Nie udało się zresetować ustawień.');
      }
      this.savingTask = null;
    },
    async testConnection(taskKey) {
      this.testing = true;
      this.testResult = null;
      this.testTaskKey = taskKey;
      try {
        this.testResult = await testAiConnection(taskKey);
      } catch (e) {
        this.testResult = {
          success: false,
          provider: taskKey && this.taskSettings[taskKey] ? this.taskSettings[taskKey].provider || this.form.provider : this.form.provider,
          model: taskKey && this.taskSettings[taskKey] ? this.taskSettings[taskKey].model || this.form.model : this.form.model,
          error: 'Nie udało się połączyć z API.',
        };
      }
      this.testing = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.stat-card {
  text-align: center;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;

  .stat-number {
    font-size: 32px;
    font-weight: bold;
    color: #409eff;
  }

  .stat-label {
    margin-top: 8px;
    color: #909399;
    font-size: 14px;
  }
}

.no-data {
  text-align: center;
  color: #909399;
  padding: 20px;
}

.task-row {
  padding: 16px 0;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }
}

.task-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.task-default-badge {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

.task-custom-badge {
  font-size: 12px;
  color: #67c23a;
  font-weight: 500;
}
</style>
